import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import type { ApiResult } from "admin/api/api_result";
import { unwrapOrThrow } from "admin/api/api_result";
import { handleGenericError } from "libs/error_handling";
import { isPlainObject } from "lodash-es";
import debounce from "lodash-es/debounce";
import noop from "lodash-es/noop";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type EqualityFn, useSelector } from "react-redux";
import { useLocation } from "react-router";
import constants from "viewer/constants";
import type { WebknossosState } from "viewer/store";
import { bigIntReplacer } from "./bigint_helpers";
import { KEYBOARD_BUTTON_LOOP_INTERVAL } from "./input";

/**
 * Hook that returns the previous value of a state or prop.
 * @param value - The current value to track
 * @param ignoreNullAndUndefined - If true, null/undefined values won't update the previous value
 * @returns The previous value, or null if no previous value exists; and a function to clear the stored previous value
 */
export function usePrevious<T>(
  value: T,
  ignoreNullAndUndefined: boolean = false,
): [T | null, () => void] {
  // Adapted from: https://usehooks.com/usePrevious/

  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef<T | null>(null);
  // Store current value in ref
  useEffect(() => {
    if (!ignoreNullAndUndefined || value != null) {
      ref.current = value;
    }
  }, [value, ignoreNullAndUndefined]);

  const clearFn = useCallback(() => {
    ref.current = null;
  }, []);
  // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return [ref.current, clearFn];
}

const extractModifierState = <K extends keyof WindowEventMap>(event: WindowEventMap[K]) => ({
  // @ts-expect-error
  Shift: event.shiftKey,
  // @ts-expect-error
  Alt: event.altKey, // This is the option key ⌥ on MacOS
  // @ts-expect-error
  ControlOrMeta: event.ctrlKey || event.metaKey,
});

/**
 * Registers window listeners that call setKeyPressed with whether targetKey is pressed.
 * @returns A function that removes the keydown and keyup listeners
 */
function subscribeToKeyPress(
  targetKey: "Shift" | "Alt" | "ControlOrMeta",
  setKeyPressed: (isPressed: boolean) => void,
) {
  function pressKey() {
    setKeyPressed(true);
    window.addEventListener("blur", releaseKey);
  }

  function releaseKey() {
    setKeyPressed(false);
    window.removeEventListener("blur", releaseKey);
  }

  // If pressed key is our target key then set to true
  const downHandler = <K extends keyof WindowEventMap>(event: WindowEventMap[K]) => {
    const modifierState = extractModifierState(event);

    if (modifierState[targetKey] === undefined) {
      // The targetKey is not a modifier. Compare to the pressed key.
      if ("key" in event && event.key === targetKey) {
        pressKey();
      }
    } else if (modifierState[targetKey]) {
      // Use the modifierState as this seems to be more robust. See
      // the other comment below which describes some edge cases
      // regarding modifiers.
      pressKey();
    }
  };

  // If released key is our target key then set to false
  const upHandler = <K extends keyof WindowEventMap>(event: WindowEventMap[K]) => {
    const modifierState = extractModifierState(event);

    if (modifierState[targetKey] === undefined) {
      // The targetKey is not a modifier. Compare to the pressed key.
      if ("key" in event && event.key === targetKey) {
        releaseKey();
      }
    } else if (!modifierState[targetKey]) {
      // The targetKey is a modifier. Use the modifierState as this
      // is more robust against pressing multiple modifiers. For example,
      // on Linux, pressing Shift and then toggling Alt, will send a release
      // of the Meta key even though that key was never touched.
      releaseKey();
    }
  };

  window.addEventListener("keydown", downHandler);
  window.addEventListener("keyup", upHandler);
  return () => {
    window.removeEventListener("keydown", downHandler);
    window.removeEventListener("keyup", upHandler);
  };
}

/**
 * Hook that tracks whether a specific modifier key (Shift, Alt, or Control/Meta) is pressed.
 * @param targetKey - The modifier key to track ("Shift", "Alt", or "ControlOrMeta")
 * @returns Boolean indicating if the key is currently pressed
 */
export function useKeyPress(targetKey: "Shift" | "Alt" | "ControlOrMeta") {
  // Adapted from: https://gist.github.com/gragland/b61b8f46114edbcf2a9e4bd5eb9f47f5

  // State for keeping track of whether key is pressed
  const [keyPressed, setKeyPressed] = useState(false);

  // Only subscribe on mount and unsubscribe on unmount
  useEffectOnlyOnce(() => subscribeToKeyPress(targetKey, setKeyPressed));
  return keyPressed;
}

/**
 * Hook that triggers a callback repeatedly while a button is pressed, with configurable delay.
 * @param triggerCallback - Function to call repeatedly, receives timeFactor and isFirst flag
 * @param repeatDelay - Delay between triggers in milliseconds
 * @returns Object with touch event handlers for the button
 */
export function useRepeatedButtonTrigger(
  triggerCallback: (timeFactor: number, isFirst: boolean) => void,
  repeatDelay: number = KEYBOARD_BUTTON_LOOP_INTERVAL,
) {
  const [isPressed, setIsPressed] = useState(false);
  const lastTrigger = useRef<number | null>(null);

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;

    if (isPressed) {
      const trigger = () => {
        timerId = setTimeout(() => {
          // The timeFactor calculation was adapted from buttonLoop() in input.ts
          const curTime = Date.now();
          // If no lastTime, assume that desired FPS is met
          const lastTime = lastTrigger.current ?? curTime - 1000 / constants.FPS;
          const elapsed = curTime - lastTime;

          triggerCallback((elapsed / 1000) * constants.FPS, lastTrigger.current == null);
          lastTrigger.current = curTime;
          trigger();
        }, repeatDelay);
      };

      trigger();
    }

    return () => {
      lastTrigger.current = null;
      clearTimeout(timerId);
    };
  }, [isPressed, triggerCallback, repeatDelay]);

  const onTouchStart = () => setIsPressed(true);
  const onTouchEnd = () => setIsPressed(false);

  return {
    // Don't do anything on click to avoid that the trigger
    // is called twice on touch start.
    onClick: noop,
    onTouchStart,
    onTouchEnd,
  };
}

/**
 * Hook that returns the current URL search parameters as an object.
 * @returns Object containing all URL search parameters
 */
export function useSearchParams() {
  const location = useLocation();
  return Object.fromEntries(new URLSearchParams(location.search).entries());
}

/**
 * Hook that triggers a re-render at specified intervals.
 * @param interval - Time between updates in milliseconds
 */
export function useUpdateEvery(interval: number) {
  const [, setSeconds] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setSeconds((seconds) => seconds + 1);
    }, interval);
    return () => window.clearInterval(intervalId);
  }, [interval]);
}

/**
 * Hook that polls a callback function at specified intervals.
 * @param callback - Async function to call repeatedly
 * @param delay - Time between polls in milliseconds, or null to stop polling
 * @param dependencies - Array of dependencies that should trigger a restart of polling
 */
export function usePolling(
  callback: () => Promise<void>,
  delay: number | null,
  dependencies: React.DependencyList = [],
) {
  const savedCallback = useRef<() => Promise<void>>(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    let killed = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let animationFrameId: number | null = null;

    async function poll() {
      if (killed) return;
      await savedCallback.current();
      if (killed) return;
      if (delay != null) {
        timeoutId = setTimeout(poll2, delay);
      }
    }

    function poll2() {
      if (killed) return;
      animationFrameId = requestAnimationFrame(poll);
    }

    poll();

    return () => {
      killed = true;
      if (animationFrameId != null) cancelAnimationFrame(animationFrameId);
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [delay, ...dependencies]);
}

/**
 * Hook that runs an effect only once when the component mounts.
 * @param callback - Function to run on mount, can return cleanup function
 */
export function useEffectOnlyOnce(callback: () => void | (() => void)) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: Explicitly run only once.
  useEffect(() => {
    return callback();
  }, []);
}

/**
 * Hook that provides both state and a ref to the current state value.
 * Useful for accessing current state in async functions.
 * @param initialValue - Initial state value
 * @returns Tuple containing [state, ref, setState]
 */
export function useStateWithRef<T>(initialValue: T) {
  // Due to the nature of hooks, the ref value might be one render cycle ahead of the state value.
  // If the ref value should preferably be one render cycle behind the state value,
  // use a different hook that uses an effect instead of a wrapped state setter.
  const [state, setState] = useState(initialValue);
  const ref = useRef(state);

  const wrappedSetState = (newState: T | ((prevState: T) => T)) => {
    setState((prevState: T) => {
      const nextState =
        typeof newState === "function" ? (newState as (prevState: T) => T)(prevState) : newState;
      ref.current = nextState;
      return nextState;
    });
  };

  return [state, ref, wrappedSetState] as const;
}

/**
 * Hook that provides a function to check if the component is still mounted.
 * @returns Function that returns true if component is mounted, false otherwise
 */
export function useIsMounted() {
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const isMounted = useCallback(() => isMountedRef.current, []);

  return isMounted;
}

/**
 * Wrapper around `useQuery` that automatically calls `handleGenericError` when the query fails.
 * This ensures the user sees a toast notification for any unhandled query error.
 * Pass `fallbackMessage` to override the default generic error message.
 */
export function useQueryWithErrorHandling<
  TQueryFnData = unknown,
  TError extends Error = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, fallbackMessage?: string) {
  const result = useQuery({
    queryKeyHashFn: (queryKey) => {
      return JSON.stringify(queryKey, (key, val) => {
        // react-query cannot hash bigints by default which is why we take care of these
        // here. Since this value won't be sent to the backend (this is only the hashed
        // query key), we don't need to use the unsignedBigIntReplacer which creates
        // a wrapper for each bigint.
        if (typeof val === "bigint") {
          return bigIntReplacer(key, val);
        }
        // The following is basically react-query's default queryKeyHashFn implementation:
        // https://github.com/TanStack/query/blob/34f7ceed09c10e4a3aa2df31a106ddf02ec4e787/packages/query-core/src/utils.ts#L232
        if (isPlainObject(val)) {
          const obj = val as Record<string, unknown>;
          return Object.keys(obj)
            .sort()
            .reduce((result: Record<string, unknown>, key: string) => {
              result[key] = obj[key];
              return result;
            }, {});
        }
        return val;
      });
    },
    ...options,
  });

  useEffect(() => {
    if (result.error != null) {
      handleGenericError(result.error, fallbackMessage ?? null);
    }
  }, [result.error, fallbackMessage]);

  return result;
}

/**
 * Wrapper around `useQuery` for rest_api.ts functions that return an `ApiResult`
 * (see libs/api_result.ts) and already retry internally. Unwraps the ApiResult so
 * `data`/`error` behave like a normal useQuery result, and disables useQuery's own
 * retry — the retrying already happened inside the queryFn, so retrying again here
 * would compound backoff on top of backoff.
 */
export function useApi<TData, TQueryKey extends QueryKey = QueryKey>(
  options: Omit<UseQueryOptions<TData, Error, TData, TQueryKey>, "queryFn" | "retry"> & {
    queryFn: () => Promise<ApiResult<TData>>;
  },
) {
  return useQuery({
    ...options,
    queryFn: async () => unwrapOrThrow(await options.queryFn()),
    retry: false,
  });
}

/**
 * Hook that provides type-safe access to the Webknossos Redux store.
 * @param fn - Selector function that receives the Webknossos state
 * @param equalityFn — An optional equality function to keep the returned value stable.
 * @returns Selected state value
 */
export function useWkSelector<T>(fn: (state: WebknossosState) => T, equalityFn?: EqualityFn<T>): T {
  return useSelector(fn, equalityFn);
}

/**
 * Hook that returns a debounced version of the passed value.
 * @param value
 * @param delay - debounce delay
 * @returns Debounced value
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  const debouncedSetter = useMemo(
    () =>
      debounce((val: T) => {
        setDebouncedValue(val);
      }, delay),
    [delay],
  );

  useEffect(() => {
    debouncedSetter(value);
  }, [value, debouncedSetter]);

  // Cancel debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSetter.cancel();
    };
  }, [debouncedSetter]);

  return debouncedValue;
}

export function useWindowWidth() {
  const [width, setWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("resize", handler);
    };
  }, []);

  return width;
}
